// components/ResultsTable.js
import React, { useEffect, useMemo } from 'react';
import './ResultsTable.css';

/**
 * Display unit for the Quantity column:
 * - 'tons' (recommended to avoid rounding tiny lb values to 0.00)
 * - 'lbs'
 */
const DISPLAY_UNIT = 'lbs';

/**
 * -----------------------------------------
 * Name aliases (material names)
 * Use this when UI labels differ from keys in factor tables.
 * -----------------------------------------
 */
const NAME_ALIASES = {
  'Magazines / Third-Class Mail': 'Magazines/Third-class Mail',
  'Magazines/Third-Class Mail': 'Magazines/Third-class Mail',
  'magazines/third-class mail': 'Magazines/Third-class Mail',
  // Add more as needed…
};
const normalizeName = (name) => NAME_ALIASES[name] || name;

/**
 * -----------------------------------------
 * Type aliases (material categories)
 * Map fuzzy UI labels to canonical keys in the factor tables.
 * -----------------------------------------
 */
const TYPE_ALIASES = {
  // Papers
  Paper: 'Paper',
  Papers: 'Paper',

  // Plastics
  Plastics: 'Plastics',
  Plastic: 'Plastics',
  'Plastic(s)': 'Plastics',
  // Treat the UI label "Mixed Plastics" as the Plastics category
  'Mixed Plastics': 'Plastics',

  // Food / Organics
  Food: 'Food Waste',
  'Food waste': 'Food Waste',
  'Food Waste': 'Food Waste',

  // Yard
  'Yard Waste': 'Yard Trimmings',
  'Yard Trimmings': 'Yard Trimmings',

  // Metals
  Metals: 'Metals',
  Metal: 'Metals',

  // Glass
  Glass: 'Glass',

  // Electronics / E-waste
  Electronics: 'Electronics',
  'E-waste': 'Electronics',
  'E-Waste': 'Electronics',

  // Construction & Demolition
  'Construction Materials': 'Construction Materials',
  Construction: 'Construction Materials',
  'C&D': 'Construction Materials',

  // Tires
  Tires: 'Tires',
  Tyres: 'Tires',

  // Mixed (umbrella)
  Mixed: 'Mixed',
  'Mixed Waste': 'Mixed',
};
const normalizeType = (type) => TYPE_ALIASES[type] || type;

/**
 * -----------------------------------------
 * 1) MTCO2E factor table (per ton)
 * null means the pathway is not applicable for that material.
 * -----------------------------------------
 * NOTE: Keep these numbers in sync with your WARM v15 tables.
 */
const MTCO2E_FACTORS = {
  Paper: {
    'Corrugated Containers': { recycled: -3.14, landfilled: 0.26, combusted: -0.49, composted: null, ad: null },
    'Magazines/Third-class Mail': { recycled: -3.07, landfilled: -0.39, combusted: -0.35, composted: null, ad: null },
    Newspaper: { recycled: -2.71, landfilled: -0.82, combusted: -0.56, composted: null, ad: null },
    'Office Paper': { recycled: -2.86, landfilled: 1.25, combusted: -0.47, composted: null, ad: null },
    Phonebooks: { recycled: -2.62, landfilled: -0.82, combusted: -0.56, composted: null, ad: null },
    Textbooks: { recycled: -3.10, landfilled: 1.25, combusted: -0.47, composted: null, ad: null },
    'Mixed Paper (general)': { recycled: -3.55, landfilled: 0.14, combusted: -0.49, composted: null, ad: null },
    'Mixed Paper (primarily residential)': { recycled: -3.55, landfilled: 0.08, combusted: -0.49, composted: null, ad: null },
    'Mixed Paper (primarily from offices)': { recycled: -3.58, landfilled: 0.18, combusted: -0.45, composted: null, ad: null },
  },
  'Food Waste': {
    'Food Waste (non-meat)': { recycled: null, landfilled: 0.54, combusted: -0.13, composted: -0.18, ad: -0.06 },
    'Food Waste (meat only)': { recycled: null, landfilled: 0.54, combusted: -0.13, composted: -0.18, ad: -0.06 },

    Beef: { recycled: null, landfilled: 0.54, combusted: -0.13, composted: -0.13, ad: -0.06 },
    Poultry: { recycled: null, landfilled: 0.54, combusted: -0.13, composted: -0.13, ad: -0.06 },
    Grains: { recycled: null, landfilled: 0.54, combusted: -0.13, composted: -0.13, ad: -0.06 },
    Bread: { recycled: null, landfilled: null, combusted: null, composted: -0.13, ad: -0.06 },
    'Fruits and Vegetables': { recycled: null, landfilled: null, combusted: null, composted: -0.13, ad: -0.06 },
    'Dairy Products': { recycled: null, landfilled: null, combusted: null, composted: -0.13, ad: -0.06 },
  },
  'Yard Trimmings': {
    Grass: { recycled: null, landfilled: 0.13, combusted: null, composted: -0.15, ad: 0.0 },
    Leaves: { recycled: null, landfilled: -0.52, combusted: null, composted: -0.15, ad: -0.14 },
    Branches: { recycled: null, landfilled: -0.50, combusted: null, composted: -0.15, ad: -0.22 },
  },
  Plastics: {
    HDPE: { recycled: -0.85, landfilled: 0.02, combusted: 1.29, composted: null, ad: null },
    PET: { recycled: -1.15, landfilled: 0.02, combusted: 1.24, composted: null, ad: null },
    LDPE: { recycled: null, landfilled: 0.02, combusted: 1.29, composted: null, ad: null },
    PP: { recycled: null, landfilled: 0.02, combusted: 1.29, composted: null, ad: null },
    PS: { recycled: null, landfilled: 0.02, combusted: 1.65, composted: null, ad: null },
    PVC: { recycled: null, landfilled: 0.02, combusted: 0.66, composted: null, ad: null },
    LLDPE: { recycled: null, landfilled: 0.02, combusted: 1.29, composted: null, ad: null },
    'Mixed Plastics': { recycled: -1.03, landfilled: 0.02, combusted: 1.26, composted: null, ad: null },
  },
  Bioplastics: {
    PLA: { recycled: null, landfilled: -1.64, combusted: -0.63, composted: -0.15, ad: null },
  },
  Metals: {
    'Aluminum Cans': { recycled: -9.13, landfilled: 0.02, combusted: null, composted: null, ad: null },
    'Steel Cans': { recycled: -1.83, landfilled: 0.02, combusted: null, composted: null, ad: null },
    'Copper Wire': { recycled: -4.49, landfilled: 0.02, combusted: null, composted: null, ad: null },
    'Aluminum Ingot': { recycled: -7.20, landfilled: 0.02, combusted: null, composted: null, ad: null },
    'Mixed Metals': { recycled: -4.39, landfilled: 0.02, combusted: -1.02, composted: null, ad: null },
  },
  Glass: {
    Glass: { recycled: -0.28, landfilled: 0.02, combusted: null, composted: null, ad: null },
  },
  Electronics: {
    'Desktop CPUs': { recycled: -1.49, landfilled: 0.02, combusted: null, composted: null, ad: null },
    'Portable Electronic Devices': { recycled: -1.07, landfilled: 0.02, combusted: null, composted: null, ad: null },
    'Flat-Panel Displays': { recycled: -1.00, landfilled: 0.02, combusted: null, composted: null, ad: null },
    'CRT Displays': { recycled: -0.57, landfilled: 0.02, combusted: null, composted: null, ad: null },
    'Electronic Peripherals': { recycled: -0.37, landfilled: 0.02, combusted: 2.08, composted: null, ad: null },
    'Hard-Copy Devices': { recycled: -0.57, landfilled: 0.02, combusted: 1.20, composted: null, ad: null },
    'Mixed Electronics': { recycled: -0.79, landfilled: 0.02, combusted: 0.39, composted: null, ad: null },
  },
  'Construction Materials': {
    'Asphalt Concrete': { recycled: -0.08, landfilled: 0.02, combusted: null, composted: null, ad: null },
    Concrete: { recycled: -0.01, landfilled: 0.02, combusted: null, composted: null, ad: null },
    Carpet: { recycled: -2.38, landfilled: 0.02, combusted: 1.10, composted: null, ad: null },
    'Dimensional Lumber': { recycled: -2.47, landfilled: -1.01, combusted: -0.58, composted: null, ad: null },
    'Asphalt Shingles': { recycled: -0.09, landfilled: 0.02, combusted: -0.35, composted: null, ad: null },
    'Clay Bricks': { recycled: null, landfilled: 0.02, combusted: null, composted: null, ad: null },
    Drywall: { recycled: 0.03, landfilled: -0.06, combusted: null, composted: null, ad: null },
    'Fiberglass Insulation': { recycled: null, landfilled: 0.02, combusted: null, composted: null, ad: null },
    'Fly Ash': { recycled: -0.87, landfilled: 0.02, combusted: null, composted: null, ad: null },
    'Medium-density Fiberboard': { recycled: -2.47, landfilled: -0.88, combusted: -0.88, composted: null, ad: null },
    'Structural Steel': { recycled: null, landfilled: null, combusted: null, composted: null, ad: null },
    'Vinyl Flooring': { recycled: null, landfilled: 0.02, combusted: null, composted: -0.31, ad: null },
    'Wood Flooring': { recycled: null, landfilled: -0.86, combusted: -0.74, composted: null, ad: null },
  },
  Tires: {
    Tires: { recycled: -0.38, landfilled: 0.02, combusted: 0.50, composted: null, ad: null },
  },
  Mixed: {
    'Mixed Recyclables': { recycled: -2.85, landfilled: null, combusted: null, composted: null, ad: null },
    'Mixed Organics': { recycled: null, landfilled: 0.21, combusted: -0.15, composted: -0.16, ad: -0.06 },
    'Mixed MSW': { recycled: null, landfilled: 0.36, combusted: 0.01, composted: null, ad: null },
  },
};

/**
 * -----------------------------------------
 * 2) Energy (BTU) factor table (per ton, MMBtu/ton)
 * -----------------------------------------
 * NOTE: Keep these numbers in sync with your WARM v15 tables.
 */
const BTU_FACTORS = {
  Paper: {
    'Corrugated Containers': { recycled: -9.97, landfilled: 0, combusted: -6.63, composted: null, ad: null },
    'Magazines/Third-class Mail': { recycled: -0.69, landfilled: 0, combusted: -4.95, composted: null, ad: null },
    Newspaper: { recycled: -16.49, landfilled: 0, combusted: -7.49, composted: null, ad: null },
    'Office Paper': { recycled: -10.08, landfilled: 0, combusted: -6.41, composted: null, ad: null },
    Phonebooks: { recycled: -11.93, landfilled: 0, combusted: -7.49, composted: null, ad: null },
    Textbooks: { recycled: -1.03, landfilled: 0, combusted: -6.41, composted: null, ad: null },
    'Mixed Paper (general)': { recycled: -0.50, landfilled: 0, combusted: null, composted: null, ad: null },
    'Mixed Paper (primarily residential)': { recycled: -27.85, landfilled: 0, combusted: null, composted: null, ad: null },
    'Mixed Paper (primarily from offices)': { recycled: -56.40, landfilled: 0, combusted: null, composted: null, ad: null },
  },
  'Food Waste': {
    'Food Waste (non-meat)': { recycled: null, landfilled: 0, combusted: -2.23, composted: 0, ad: 0 },
    'Food Waste (meat only)': { recycled: null, landfilled: 0, combusted: -2.23, composted: 0, ad: 0 },

    Beef: { recycled: null, landfilled: 0, combusted: -2.23, composted: 0, ad: 0 },
    Poultry: { recycled: null, landfilled: 0, combusted: -2.23, composted: 0, ad: 0 },
    Grains: { recycled: null, landfilled: 0, combusted: -2.23, composted: 0, ad: 0 },
    Bread: { recycled: null, landfilled: null, combusted: -2.23, composted: 0, ad: 0 },
    'Fruits and Vegetables': { recycled: null, landfilled: null, combusted: -2.23, composted: 0, ad: 0 },
    'Dairy Products': { recycled: null, landfilled: null, combusted: -2.23, composted: 0, ad: 0 },
  },
  'Yard Trimmings': {
    Grass: { recycled: null, landfilled: 0, combusted: null, composted: 0, ad: 0 },
    Leaves: { recycled: null, landfilled: 0, combusted: null, composted: 0, ad: 0 },
    Branches: { recycled: null, landfilled: 0, combusted: null, composted: 0, ad: 0 },
  },
  Plastics: {
    HDPE: { recycled: -50.24, landfilled: 0, combusted: -18.83, composted: null, ad: null },
    PET: { recycled: -31.92, landfilled: 0, combusted: -9.99, composted: null, ad: null },
    LDPE: { recycled: null, landfilled: 0, combusted: -18.72, composted: null, ad: null },
    PP: { recycled: null, landfilled: 0, combusted: -18.79, composted: null, ad: null },
    PS: { recycled: null, landfilled: 0, combusted: -16.96, composted: null, ad: null },
    PVC: { recycled: null, landfilled: 0, combusted: -7.42, composted: null, ad: null },
    LLDPE: { recycled: null, landfilled: 0, combusted: -18.72, composted: null, ad: null },
    'Mixed Plastics': { recycled: null, landfilled: 0, combusted: null, composted: null, ad: null }, // N/A in current table
  },
  Metals: {
    'Aluminum Cans': { recycled: -152.76, landfilled: 0, combusted: 0.32, composted: null, ad: null },
    'Steel Cans': { recycled: -19.97, landfilled: 0, combusted: -17.41, composted: null, ad: null },
    'Copper Wire': { recycled: -82.59, landfilled: 0, combusted: 0.26, composted: null, ad: null },
    'Aluminum Ingot': { recycled: null, landfilled: 0, combusted: null, composted: null, ad: null },
    'Mixed Metals': { recycled: null, landfilled: 0, combusted: null, composted: null, ad: null },
  },
  Glass: {
    Glass: { recycled: -2.13, landfilled: 0, combusted: 0.22, composted: null, ad: null },
  },
  Electronics: {
    'Desktop CPUs': { recycled: -21.29, landfilled: 0, combusted: null, composted: null, ad: null },
    'Portable Electronic Devices': { recycled: -21.14, landfilled: 0, combusted: null, composted: null, ad: null },
    'Flat-Panel Displays': { recycled: -15.23, landfilled: 0, combusted: null, composted: null, ad: null },
    'CRT Displays': { recycled: -8.09, landfilled: 0, combusted: null, composted: null, ad: null },
    'Electronic Peripherals': { recycled: null, landfilled: 0, combusted: null, composted: null, ad: null },
    'Hard-Copy Devices': { recycled: null, landfilled: 0, combusted: null, composted: null, ad: null },
    'Mixed Electronics': { recycled: null, landfilled: 0, combusted: null, composted: null, ad: null },
  },
  'Construction Materials': {
    'Asphalt Concrete': { recycled: -1.22, landfilled: 0, combusted: null, composted: null, ad: null },
    Concrete: { recycled: -0.11, landfilled: 0, combusted: null, composted: null, ad: null },
    Carpet: { recycled: -21.46, landfilled: 0, combusted: null, composted: null, ad: null },
    'Dimensional Lumber': { recycled: 0.59, landfilled: 0, combusted: -7.82, composted: null, ad: null },
    'Asphalt Shingles': { recycled: null, landfilled: 0, combusted: null, composted: null, ad: null },
    'Clay Bricks': { recycled: null, landfilled: 0, combusted: null, composted: null, ad: null },
    Drywall: { recycled: null, landfilled: 0, combusted: null, composted: null, ad: null },
    'Fiberglass Insulation': { recycled: null, landfilled: 0, combusted: null, composted: null, ad: null },
    'Fly Ash': { recycled: null, landfilled: 0, combusted: null, composted: null, ad: null },
    'Medium-density Fiberboard': { recycled: null, landfilled: 0, combusted: null, composted: null, ad: null },
    'Structural Steel': { recycled: null, landfilled: 0, combusted: null, composted: null, ad: null },
    'Vinyl Flooring': { recycled: null, landfilled: 0, combusted: null, composted: null, ad: null },
    'Wood Flooring': { recycled: null, landfilled: 0, combusted: null, composted: null, ad: null },
  },
  Tires: {
    Tires: { recycled: 1.74, landfilled: 0, combusted: null, composted: null, ad: null },
  },
  Mixed: {
    'Mixed Recyclables': { recycled: -11.85, landfilled: null, combusted: null, composted: null, ad: null },
    'Mixed Organics': { recycled: null, landfilled: 0, combusted: null, composted: 0, ad: 0 },
    'Mixed MSW': { recycled: null, landfilled: 0, combusted: null, composted: null, ad: null },
  },
};

/**
 * Build a human-readable “formula JSON” from MTCO2E factors.
 * This avoids maintaining duplicate hard-coded formula tables.
 * (Display-only; calculations below always use tons.)
 */
const buildFormulaJson = (factors) => {
  const out = {};
  for (const [type, mats] of Object.entries(factors)) {
    out[type] = {};
    for (const [mat, paths] of Object.entries(mats)) {
      const obj = {};
      if (paths.recycled !== undefined) obj['Lbs Recycled'] = paths.recycled === null ? 'N/A' : `(inputLbs / 2000) * ${paths.recycled}`;
      if (paths.landfilled !== undefined) obj['Lbs Landfilled'] = paths.landfilled === null ? 'N/A' : `(inputLbs / 2000) * ${paths.landfilled}`;
      if (paths.combusted !== undefined) obj['Lbs Combusted'] = paths.combusted === null ? 'N/A' : `(inputLbs / 2000) * ${paths.combusted}`;
      if (paths.composted !== undefined) obj['Lbs Composted'] = paths.composted === null ? 'N/A' : `(inputLbs / 2000) * ${paths.composted}`;
      if (paths.ad !== undefined) obj['Lbs Anaerobically Digested'] = paths.ad === null ? 'N/A' : `(inputLbs / 2000) * ${paths.ad}`;
      out[type][mat] = obj;
    }
  }
  return out;
};
export const FORMULA_JSON = buildFormulaJson(MTCO2E_FACTORS);

/**
 * Factor getters (normalize both type and name).
 * Also warn in DevTools when something doesn’t match.
 */
const getMTCO2EFactors = (materialType, material) => {
  const t = normalizeType(materialType);
  const table = MTCO2E_FACTORS[t] || {};
  const key = normalizeName(material);
  const value = table[key];
  if (!value) {
    console.warn('[MTCO2E_FACTORS] not found:', {
      incomingType: materialType,
      normalizedType: t,
      material,
      normalizedMaterial: key,
    });
  }
  return value || { recycled: null, landfilled: null, combusted: null, composted: null, ad: null };
};

const getBTUFactors = (materialType, material) => {
  const t = normalizeType(materialType);
  const table = BTU_FACTORS[t] || {};
  const key = normalizeName(material);
  const value = table[key];
  if (!value) {
    console.warn('[BTU_FACTORS] not found:', {
      incomingType: materialType,
      normalizedType: t,
      material,
      normalizedMaterial: key,
    });
  }
  return value || { recycled: null, landfilled: null, combusted: null, composted: null, ad: null };
};

/**
 * Row calculations using a single user input (tons) times factors.
 * These are displayed per-path; totals may use either split-pathway
 * or a fallback “default path” if no split is provided.
 */
const computeMTCO2EByPath = (tons, f) => ({
  recycled: f.recycled === null ? null : tons * f.recycled,
  landfilled: f.landfilled === null ? null : tons * f.landfilled,
  combusted: f.combusted === null ? null : tons * f.combusted,
  composted: f.composted === null ? null : tons * f.composted,
  ad: f.ad === null ? null : tons * f.ad,
});

const sumNonNull = (...vals) =>
  vals.reduce((s, v) => (v === null || Number.isNaN(v) ? s : s + v), 0);

/**
 * Pick a default pathway when no split-pathway tons are provided.
 * Priority: recycled > composted > ad > combusted > landfilled.
 * Returns a key from {'recycled','composted','ad','combusted','landfilled'} or null.
 */
const PATH_ORDER = ['recycled', 'composted', 'ad', 'combusted', 'landfilled'];
const pickDefaultPath = (mtc, btu) => {
  // Prefer a path that has an MTCO2E factor; BTU may be null for some paths, which is acceptable.
  for (const p of PATH_ORDER) {
    if (mtc[p] !== null && mtc[p] !== undefined) return p;
  }
  // As a last resort, if all MTCO2E are null but BTU exists on some path, pick that.
  for (const p of PATH_ORDER) {
    if (btu[p] !== null && btu[p] !== undefined) return p;
  }
  return null;
};

/**
 * Define the visual order for pathway columns and their labels.
 * We will render both headers and cells using this single source of truth.
 */
const DISPLAY_PATH_ORDER = [
  { key: 'recycled',  label: 'Recycled'  },
  { key: 'composted', label: 'Composted' },
  { key: 'ad',        label: 'AD'        },
  { key: 'combusted', label: 'Combusted' },
  { key: 'landfilled',label: 'Landfilled'}
];

/**
 * Render helper with slightly higher precision by default
 * so that tiny values (e.g., 1 lb worth of impact) are visible.
 */
const renderNumber = (v, digits = 4) =>
  v === null || typeof v !== 'number' || Number.isNaN(v) ? 'N/A' : v.toFixed(digits);

/**
 * ResultsTable
 * - Renders per-material rows using factors
 * - Accumulates grand totals
 * - Reports grand totals up to parent via `onTotalsChange`
 *
 * Hooks order is unconditional:
 * - We always call useMemo and useEffect, even if we end up returning null (no render).
 */
const ResultsTable = ({ results, isVisible, postTableSlot, onTotalsChange }) => {
  // Build rows and totals in a memo so hooks can be called unconditionally
  const { tableRows, totals } = useMemo(() => {
    const totalsAcc = {
      quantityDisplayUnit: 0,
      totalMTCO2E: 0,
      totalMillionBTU: 0,
    };
    const rows = [];

    // If nothing to show, return empty rows and zero totals
    if (!isVisible || !results) {
      return { tableRows: rows, totals: totalsAcc };
    }

    // ==== Core loop: compute rows and accumulate totals ====
    Object.entries(results).forEach(([materialType, materials]) => {
      Object.entries(materials || {}).forEach(([material, row]) => {
        if (!row) return;

        // Prefer a single input value; support both tons and lbs.
        const inputLbs = Number(row.inputLbs) || 0;
        const inputTons =
          Number(row.inputTons) ||
          (inputLbs ? inputLbs / 2000 : 0) ||
          // Legacy support: sum split-pathway tons back up if present
          ['tonsRecycled', 'tonsLandfilled', 'tonsCombusted', 'tonsComposted', 'tonsAnaerobicDigested']
            .map((k) => Number(row[k]) || 0)
            .reduce((a, b) => a + b, 0) ||
          0;

        if (inputTons <= 0) return;

        const mtc = getMTCO2EFactors(materialType, material);
        const btu = getBTUFactors(materialType, material);

        // Split-pathway tons (optional)
        const tonsRecycled = Number(row.tonsRecycled) || 0;
        const tonsLandfilled = Number(row.tonsLandfilled) || 0;
        const tonsCombusted = Number(row.tonsCombusted) || 0;
        const tonsComposted = Number(row.tonsComposted) || 0;
        const tonsAnaerobicDigested = Number(row.tonsAnaerobicDigested) || 0;

        // Determine if we should use split-pathway totals
        const isSplitPathway =
          tonsRecycled > 0 || tonsLandfilled > 0 || tonsCombusted > 0 || tonsComposted > 0 || tonsAnaerobicDigested > 0;

        // Per-path impacts for display columns (computed from the single inputTons)
        const impacts = computeMTCO2EByPath(inputTons, mtc);

        // ======== TOTALS LOGIC ========
        let totalMTCO2E;
        let totalBTU;

        if (isSplitPathway) {
          totalMTCO2E = sumNonNull(
            mtc.recycled   == null ? null : tonsRecycled          * mtc.recycled,
            mtc.landfilled == null ? null : tonsLandfilled        * mtc.landfilled,
            mtc.combusted  == null ? null : tonsCombusted         * mtc.combusted,
            mtc.composted  == null ? null : tonsComposted         * mtc.composted,
            mtc.ad         == null ? null : tonsAnaerobicDigested * mtc.ad
          );
          totalBTU = sumNonNull(
            btu.recycled   == null ? null : tonsRecycled          * btu.recycled,
            btu.landfilled == null ? null : tonsLandfilled        * btu.landfilled,
            btu.combusted  == null ? null : tonsCombusted         * btu.combusted,
            btu.composted  == null ? null : tonsComposted         * btu.composted,
            btu.ad         == null ? null : tonsAnaerobicDigested * btu.ad
          );
        } else {
          const defaultPath = pickDefaultPath(mtc, btu);
          if (defaultPath) {
            const mtcFactor = mtc[defaultPath];
            const btuFactor = btu[defaultPath];
            totalMTCO2E = (mtcFactor == null) ? null : inputTons * mtcFactor;
            totalBTU    = (btuFactor == null) ? null : inputTons * btuFactor;
          } else {
            totalMTCO2E = null;
            totalBTU = null;
          }
        }
        // ======== END TOTALS LOGIC ========

        // Accumulate grand totals (quantity in chosen display unit)
        totalsAcc.quantityDisplayUnit += DISPLAY_UNIT === 'lbs'
          ? (inputLbs || inputTons * 2000)
          : inputTons;

        totalsAcc.totalMTCO2E     += (totalMTCO2E ?? 0);
        totalsAcc.totalMillionBTU += (totalBTU ?? 0);

        const displayQty = DISPLAY_UNIT === 'lbs'
          ? (inputLbs || inputTons * 2000)
          : inputTons;

        rows.push(
          <tr key={`${materialType}-${material}`}>
            <td className="material-type-cell">{materialType}</td>
            <td className="material-name-cell">{material}</td>
            <td className="number-cell">
              {renderNumber(displayQty, DISPLAY_UNIT === 'lbs' ? 2 : 4)}
            </td>

            {/* Render pathway columns strictly following DISPLAY_PATH_ORDER */}
            {DISPLAY_PATH_ORDER.map(({ key }) => (
              <td className="number-cell" key={`${material}-${key}`}>
                {renderNumber(impacts[key])}
              </td>
            ))}

            <td className="number-cell">{renderNumber(totalMTCO2E)}</td>
            <td className="number-cell">{renderNumber(totalBTU)}</td>
          </tr>
        );
      });
    });

    return { tableRows: rows, totals: totalsAcc };
  }, [isVisible, results]);

  // Notify parent of the latest grand totals after each render (unconditional hook)
  useEffect(() => {
    if (onTotalsChange) {
      onTotalsChange({
        mtco2e: totals.totalMTCO2E,
        mmbtu: totals.totalMillionBTU,
      });
    }
  }, [totals.totalMTCO2E, totals.totalMillionBTU, onTotalsChange]);

  // If not visible or no results, render nothing (hooks above still ran in order)
  if (!isVisible || !results) return null;

  const quantityHeader =
    DISPLAY_UNIT === 'lbs' ? 'Quantity (Lbs)' : 'Quantity (Tons)';

  return (
    <>
      <div className="results-header">
        <h3 className="results-title">Calculation Results</h3>
        <p className="results-subtitle">
          Impacts per row = Quantity (tons) × pathway factors (WARM v15).
        </p>
      </div>

      <div className="results-table-container">
        <table className="results-table">
          <thead>
            <tr>
              <th>Material Type</th>
              <th>Material</th>
              <th>{quantityHeader}</th>
              {DISPLAY_PATH_ORDER.map((p) => (
                <th key={`hdr-${p.key}`}>
                  {p.label}<br />Impact (MTCO₂E)
                </th>
              ))}
              <th>Total MTCO₂E</th>
              <th>Total Million BTU</th>
            </tr>
          </thead>
          <tbody>
            {tableRows}
            <tr className="totals-row">
              <td colSpan="2" className="totals-label">TOTALS</td>
              <td className="number-cell totals-cell">
                {DISPLAY_UNIT === 'lbs'
                  ? totals.quantityDisplayUnit.toFixed(2)
                  : totals.quantityDisplayUnit.toFixed(4)}
              </td>
              {DISPLAY_PATH_ORDER.map(({ key }) => (
                <td key={`totals-${key}`} className="number-cell totals-cell">—</td>
              ))}
              <td className="number-cell totals-cell">{renderNumber(totals.totalMTCO2E)}</td>
              <td className="number-cell totals-cell">{renderNumber(totals.totalMillionBTU)}</td>
            </tr>
          </tbody>
        </table>

        {/* Slot for extra content directly below the table (e.g., EquivalencyPanel) */}
        {postTableSlot}
        
        <div className="results-notes">
          <p>
            <strong>Notes:</strong> Factors are from EPA WARM v15. Null factors are shown as “N/A”
            and excluded from totals. If a row does not appear, its quantity was 0 or missing.
            Quantity column shows {DISPLAY_UNIT.toUpperCase()} for readability.
          </p>
          <p>
            Each path column shows the impact <em>if 100% of the quantity followed that single path</em>.
            Totals reflect the <em>actual split</em> (if provided), or a single <em>default path</em> when no split is provided
            with the priority:
            <code> Recycled &gt; Composted &gt; AD &gt; Combusted &gt; Landfilled</code>.
          </p>
          
          <p>
            <strong>Sign convention (Calculation Results):</strong>
            {' '}Negative = environmental benefit; positive = environmental cost.
          </p>

          <p>
          <strong>In the Environmental Equivalencies panel</strong>, only items with the Benefit badge represent environmental benefits; others indicate environmental costs.          </p>
        </div>
        
      </div>
    </>
  );
};

export default ResultsTable;
